import { useState, useEffect } from 'react'
import api from '../../services/api'
import toast from 'react-hot-toast'
import { Calendar, CheckCircle, XCircle, Clock, Save, Users, RotateCcw, BookOpen, ChevronDown } from 'lucide-react'
import LoadingSpinner from '../../components/LoadingSpinner'

function getInitials(first, last) {
  return `${(first || '')[0] || ''}${(last || '')[0] || ''}`.toUpperCase()
}

const STATUS_OPTIONS = [
  { value: 'present', label: 'Present', short: 'P', icon: CheckCircle, activeClass: 'bg-green-500 text-white', textClass: 'text-green-600' },
  { value: 'absent',  label: 'Absent',  short: 'A', icon: XCircle,     activeClass: 'bg-red-500 text-white',   textClass: 'text-red-600' },
  { value: 'late',    label: 'Late',    short: 'L', icon: Clock,        activeClass: 'bg-yellow-500 text-white', textClass: 'text-yellow-600' },
]

export default function Attendance() {
  const [courses, setCourses] = useState([])
  const [selectedCourse, setSelectedCourse] = useState('')
  const [students, setStudents] = useState([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0])
  const [attendance, setAttendance] = useState({})

  useEffect(() => { fetchCourses() }, [])
  useEffect(() => { if (selectedCourse) fetchStudents() }, [selectedCourse])

  const fetchCourses = async () => {
    try {
      const { data } = await api.get('/trainers/courses')
      setCourses(data)
      if (data.length > 0) setSelectedCourse(data[0].id)
    } catch {
      toast.error('Failed to load courses')
      setLoading(false)
    }
  }

  const fetchStudents = async () => {
    try {
      setLoading(true)
      const { data } = await api.get(`/trainers/courses/${selectedCourse}/students`)
      setStudents(data)
      const init = {}
      data.forEach(s => { init[s.id] = 'present' })
      setAttendance(init)
    } catch {
      toast.error('Failed to load students')
    } finally {
      setLoading(false)
    }
  }

  const markAll = (status) => {
    const next = {}
    students.forEach(s => { next[s.id] = status })
    setAttendance(next)
    toast.success(`All marked as ${status}`)
  }

  const handleSubmitAttendance = async () => {
    if (!Object.keys(attendance).length) {
      toast.error('No students to record')
      return
    }
    setSaving(true)
    try {
      const records = Object.entries(attendance).map(([studentId, status]) => ({
        student_id: parseInt(studentId),
        course_id: parseInt(selectedCourse),
        date: selectedDate,
        status,
      }))
      await api.post('/trainers/attendance', { records })
      toast.success(`Attendance saved for ${records.length} student${records.length !== 1 ? 's' : ''}!`)
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to record attendance')
    } finally {
      setSaving(false)
    }
  }

  const summary = { present: 0, absent: 0, late: 0 }
  Object.values(attendance).forEach(s => { if (summary[s] !== undefined) summary[s]++ })

  if (loading) return <LoadingSpinner label="Loading students" />

  const inputCls = "w-full bg-white/5 border border-white/10 text-white text-sm font-sans px-3 py-2.5 focus:outline-none focus:border-cinema-glow/40 transition-colors"

  return (
    <div className="space-y-6 pb-24 sm:pb-6">
      {/* Header */}
      <div className="cinema-page-header">
        <div>
          <span className="cinema-label block mb-0.5">Trainer Portal</span>
          <h1 className="font-display text-xl text-white font-light">Mark Attendance</h1>
        </div>
      </div>

      {/* Course + Date */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-6">
        <div>
          <label className="cinema-label flex items-center gap-1 mb-2"><BookOpen size={11} /> Course</label>
          <select value={selectedCourse} onChange={e => setSelectedCourse(e.target.value)} className={inputCls}>
            {courses.map(c => <option key={c.id} value={c.id} className="bg-gray-900">{c.title}</option>)}
          </select>
        </div>
        <div>
          <label className="cinema-label flex items-center gap-1 mb-2"><Calendar size={11} /> Date</label>
          <input type="date" value={selectedDate} onChange={e => setSelectedDate(e.target.value)} className={inputCls} />
        </div>
      </div>

      {students.length === 0 ? (
        <div className="cinema-empty">
          <Calendar size={32} className="mx-auto mb-3 text-cinema-muted/20" />
          <p className="font-sans text-sm text-cinema-muted">No students enrolled in this course</p>
        </div>
      ) : (
        <>
          {/* Summary + quick mark */}
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3 flex-wrap">
              <span className="flex items-center gap-1.5 font-sans text-xs font-medium text-green-400 bg-green-500/10 border border-green-500/20 px-2.5 py-1">
                <CheckCircle size={11} /> {summary.present}
              </span>
              <span className="flex items-center gap-1.5 font-sans text-xs font-medium text-red-400 bg-red-500/10 border border-red-500/20 px-2.5 py-1">
                <XCircle size={11} /> {summary.absent}
              </span>
              <span className="flex items-center gap-1.5 font-sans text-xs font-medium text-yellow-400 bg-yellow-500/10 border border-yellow-500/20 px-2.5 py-1">
                <Clock size={12} /> {summary.late}
              </span>
              <span className="font-sans text-xs text-cinema-muted font-medium">{students.length} total</span>
            </div>
            <div className="flex gap-2">
              <button onClick={() => markAll('present')} className="font-sans text-xs px-3.5 py-1.5 cinema-glass border border-green-500/20 text-green-400 hover:bg-green-500/10 transition-colors font-medium">All Present</button>
              <button onClick={() => markAll('absent')} className="font-sans text-xs px-3.5 py-1.5 cinema-glass border border-red-500/20 text-red-400 hover:bg-red-500/10 transition-colors font-medium">All Absent</button>
            </div>
          </div>

          {/* Student list */}
          <div className="cinema-list">
            {students.map((student) => {
              const current = attendance[student.id] || 'present'
              return (
                <div key={student.id} className="cinema-list-row">
                  <div className="w-8 h-8 rounded-sm bg-cinema-glow/15 flex items-center justify-center text-cinema-glow font-display text-xs font-semibold shrink-0">
                    {getInitials(student.first_name, student.last_name)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-sans text-sm sm:text-base text-white font-medium truncate">{student.first_name} {student.last_name}</p>
                    <p className="font-sans text-xs text-cinema-muted truncate hidden sm:block mt-0.5">{student.email}</p>
                  </div>
                  <div className="flex border border-white/10 overflow-hidden shrink-0">
                    {STATUS_OPTIONS.map(opt => {
                      const isActive = current === opt.value
                      const activeMap = { present: 'bg-green-500/20 text-green-400 border-green-500/30', absent: 'bg-red-500/20 text-red-400 border-red-500/30', late: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' }
                      return (
                        <button
                          key={opt.value}
                          onClick={() => setAttendance({ ...attendance, [student.id]: opt.value })}
                          className={`flex items-center gap-1 px-2.5 sm:px-3 py-2 font-sans text-xs font-medium transition-colors border-r border-white/10 last:border-r-0
                            ${isActive ? activeMap[opt.value] : 'text-cinema-muted hover:text-white hover:bg-white/5'}`}
                          title={opt.label}
                        >
                          <opt.icon size={13} className="shrink-0" />
                          <span className="hidden sm:inline">{opt.label}</span>
                          <span className="sm:hidden">{opt.short}</span>
                        </button>
                      )
                    })}
                  </div>
                </div>
              )
            })}
          </div>

          {/* Desktop actions */}
          <div className="hidden sm:flex justify-end gap-3">
            <button onClick={() => markAll('present')} className="cinema-btn-ghost text-xs px-4 py-2.5 min-h-0 flex items-center gap-2">
              <RotateCcw size={13} /> Reset to Present
            </button>
            <button onClick={handleSubmitAttendance} disabled={saving} className="cinema-btn-primary text-xs px-6 py-2.5 min-h-0 flex items-center gap-2 disabled:opacity-50">
              {saving ? <><LoadingSpinner size="sm" inline /> Saving...</>
                : <><Save size={13} /> Save Attendance</>}
            </button>
          </div>

          {/* Mobile sticky bar */}
          <div className="sm:hidden fixed bottom-0 left-0 right-0 p-3 flex gap-2 z-20" style={{ background: 'rgba(10,12,20,0.95)', borderTop: '1px solid rgba(255,255,255,0.08)' }}>
            <button onClick={() => markAll('present')} className="cinema-btn-ghost p-3 min-h-0 shrink-0">
              <RotateCcw size={14} />
            </button>
            <button onClick={handleSubmitAttendance} disabled={saving} className="cinema-btn-primary flex-1 text-xs min-h-0 flex items-center justify-center gap-2 disabled:opacity-50">
              {saving ? <><LoadingSpinner size="sm" inline /> Saving...</>
                : <><Save size={13} /> Save ({students.length} students)</>}
            </button>
          </div>
        </>
      )}
    </div>
  )
}

